import React, { useState, useEffect } from 'react';
import './App.css';

// Number counting animation hook
function useCountUp(end, duration = 1000) {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (end === 0) {
      setCount(0);
      return;
    }
    
    let startTime;
    let animationFrame;
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const percentage = Math.min(progress / duration, 1);
      
      setCount(Math.floor(end * percentage));
      
      if (percentage < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);
  
  return count;
}

function App() {
  const [step, setStep] = useState('landing');
  const [formData, setFormData] = useState({
    monthlyRevenue: '',
    monthlyExpenses: '',
    currentCash: '',
    paymentTerms: '',
    forecastAbility: '',
    cashFlowPattern: ''
  });
  const [results, setResults] = useState(null);

  // Move hooks to top level - always call them, but only use values when needed
  const runwayForAnimation = results ? (results.daysOfRunway === 999 ? 90 : results.daysOfRunway) : 0;
  const burnForAnimation = results ? Math.abs(results.dailyBurn) : 0;
  const riskForAnimation = results ? results.riskScore : 0;
  
  const animatedRunway = useCountUp(runwayForAnimation, 1500);
  const animatedBurn = useCountUp(burnForAnimation, 1500);
  const animatedRisk = useCountUp(riskForAnimation, 1500);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateResults = () => {
    const revenue = parseFloat(formData.monthlyRevenue) || 0;
    const expenses = parseFloat(formData.monthlyExpenses) || 0;
    const cash = parseFloat(formData.currentCash) || 0;
    
    const monthlyBurn = expenses - revenue;
    const daysOfRunway = monthlyBurn > 0 ? Math.round((cash / monthlyBurn) * 30) : 999;
    const dailyBurn = Math.round(monthlyBurn / 30);
    
    // Calculate risk score
    let riskScore = 0;
    if (daysOfRunway < 30) riskScore += 40;
    else if (daysOfRunway < 60) riskScore += 25;
    
    const paymentDays = parseInt(formData.paymentTerms) || 0;
    if (paymentDays > 30) riskScore += 15;
    
    if (formData.forecastAbility !== 'yes') riskScore += 20;
    if (formData.cashFlowPattern === 'unpredictable' || formData.cashFlowPattern === 'unknown') riskScore += 15;
    
    const riskLevel = riskScore >= 70 ? 'High Risk' : riskScore >= 40 ? 'Moderate Risk' : 'Low Risk';
    
    // Calculate cash leak amounts
    const paymentLeakAmount = paymentDays > 30 ? Math.round((revenue * (paymentDays - 15)) / 30) : 0;
    
    setResults({
      daysOfRunway,
      dailyBurn,
      riskScore,
      riskLevel,
      monthlyBurn,
      paymentDays,
      paymentLeakAmount,
      forecastAbility: formData.forecastAbility,
      cashFlowPattern: formData.cashFlowPattern,
      cash
    });
    
    setStep('results');
  };

  const isFormComplete = () => {
    return formData.monthlyRevenue && 
           formData.monthlyExpenses && 
           formData.currentCash && 
           formData.paymentTerms && 
           formData.forecastAbility && 
           formData.cashFlowPattern;
  };

  const getLeaks = () => {
    if (!results) return [];
    
    const leaks = [];
    
    if (results.paymentDays > 30) {
      leaks.push({
        title: 'Slow Customer Payments',
        description: `${results.paymentDays}-day payment terms are bleeding approximately $${results.paymentLeakAmount.toLocaleString()}/month`,
        education: 'Every day customers take to pay you is a day your cash is sitting in their account instead of yours. The longer the gap between when you deliver value and when you get paid, the more working capital you need to keep the lights on.',
        benchmark: 'Industry best practice: Net-15 for most B2B services, immediate payment for consumer transactions',
        action: 'Call your top 3 customers this week and negotiate payment terms to net-15. Offer a 2% discount for early payment.'
      });
    }
    
    if (results.forecastAbility !== 'yes') {
      leaks.push({
        title: 'No Cash Visibility',
        description: "You can't predict your cash position 90 days out",
        education: 'Without a forecast, you\'re managing cash by looking in the rearview mirror. By the time you see a problem, it\'s already too late to fix it without panic moves.',
        benchmark: 'Healthy businesses forecast cash 90 days out and update weekly',
        action: 'Build a simple 90-day cash forecast this week. Track: expected revenue, known expenses, payment timing.'
      });
    }
    
    if (results.cashFlowPattern === 'unpredictable' || results.cashFlowPattern === 'unknown') {
      leaks.push({
        title: 'Unpredictable Revenue',
        description: 'Your revenue swings make cash management nearly impossible',
        education: 'When revenue is unpredictable, you need a larger cash buffer and better early warning systems. Otherwise, you\'re constantly scrambling to cover expenses.',
        benchmark: 'Target: Revenue predictability within ±15% month-over-month',
        action: 'Track your pipeline weekly to spot revenue gaps 30 days out. This gives you time to fill holes before they become crises.'
      });
    }
    
    if (results.daysOfRunway < 60) {
      leaks.push({
        title: 'No Cash Buffer',
        description: `With only ${results.daysOfRunway} days of runway, you're operating on fumes`,
        education: 'A healthy business needs 60-90 days of cash buffer to handle unexpected expenses, slow months, and growth investments without panic.',
        benchmark: `For your burn rate, you should have at least $${Math.abs(results.monthlyBurn * 2).toLocaleString()} in reserves`,
        action: 'Build a 60-day cash buffer by: (1) accelerating collections, (2) pausing non-essential spending, (3) considering a line of credit as backup.'
      });
    }
    
    return leaks.slice(0, 3);
  };

  if (step === 'landing') {
    return (
      <div className="container">
        <div className="card fade-in">
          <div className="header">
            <h1 className="main-heading">How many days until you run out of cash?</h1>
            <p className="subheading">Find out in 60 seconds</p>
          </div>
          
          <div className="content">
            <p className="body-text">
              Most business owners operate with 15-30 days of visibility into their cash position. 
              That's not enough time to fix problems before they become crises.
            </p>
            <p className="body-text">
              This calculator reveals exactly where you stand, and shows you the specific leaks 
              bleeding cash from your business right now.
            </p>
            
            <button 
              className="primary-button"
              onClick={() => setStep('questions')}
            >
              Calculate My Runway
            </button>
            
            <p className="disclaimer">No email required. Results instantly.</p>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'questions') {
    return (
      <div className="container">
        <div className="card fade-in">
          <div className="progress-bar">
            <div className="progress-fill"></div>
          </div>
          
          <h2 className="section-heading">Answer 6 quick questions</h2>
          
          <div className="questions-container">
            <div className="question-block">
              <label className="label">1. What's your approximate monthly revenue?</label>
              <div className="input-wrapper">
                <span className="dollar-sign">$</span>
                <input
                  type="number"
                  className="input"
                  value={formData.monthlyRevenue}
                  onChange={(e) => handleInputChange('monthlyRevenue', e.target.value)}
                  placeholder="50000"
                />
              </div>
            </div>

            <div className="question-block">
              <label className="label">2. What are your total monthly expenses?</label>
              <div className="input-wrapper">
                <span className="dollar-sign">$</span>
                <input
                  type="number"
                  className="input"
                  value={formData.monthlyExpenses}
                  onChange={(e) => handleInputChange('monthlyExpenses', e.target.value)}
                  placeholder="55000"
                />
              </div>
            </div>

            <div className="question-block">
              <label className="label">3. How much cash do you have in the bank right now?</label>
              <div className="input-wrapper">
                <span className="dollar-sign">$</span>
                <input
                  type="number"
                  className="input"
                  value={formData.currentCash}
                  onChange={(e) => handleInputChange('currentCash', e.target.value)}
                  placeholder="25000"
                />
              </div>
            </div>

            <div className="question-block">
              <label className="label">4. How long does it typically take customers to pay you?</label>
              <select
                className="select"
                value={formData.paymentTerms}
                onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
              >
                <option value="">Select...</option>
                <option value="0">Immediately (credit card, cash)</option>
                <option value="15">Net-15 (15 days)</option>
                <option value="30">Net-30 (30 days)</option>
                <option value="45">Net-45 (45 days)</option>
                <option value="60">Net-60+ (60+ days)</option>
              </select>
            </div>

            <div className="question-block">
              <label className="label">5. Can you predict your cash position 90 days from now?</label>
              <select
                className="select"
                value={formData.forecastAbility}
                onChange={(e) => handleInputChange('forecastAbility', e.target.value)}
              >
                <option value="">Select...</option>
                <option value="yes">Yes, confidently</option>
                <option value="somewhat">Somewhat</option>
                <option value="not-really">Not really</option>
                <option value="no">No idea</option>
              </select>
            </div>

            <div className="question-block">
              <label className="label">6. Which best describes your cash flow?</label>
              <select
                className="select"
                value={formData.cashFlowPattern}
                onChange={(e) => handleInputChange('cashFlowPattern', e.target.value)}
              >
                <option value="">Select...</option>
                <option value="consistent">Consistent and predictable</option>
                <option value="variation">Some month-to-month variation</option>
                <option value="unpredictable">Very unpredictable</option>
                <option value="unknown">Don't know / haven't tracked it</option>
              </select>
            </div>

            <button 
              className="primary-button"
              onClick={calculateResults}
              disabled={!isFormComplete()}
              style={{
                opacity: isFormComplete() ? 1 : 0.5,
                cursor: isFormComplete() ? 'pointer' : 'not-allowed'
              }}
            >
              See My Results
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'results' && results) {
    const leaks = getLeaks();

    return (
      <div className="container">
        <div className="card fade-in">
          {/* THE REVEAL */}
          <div className="reveal-section">
            <h2 className="results-heading">Your Cash Reality</h2>
            
            <div className="metrics-grid">
              <div className="metric-card hover-lift">
                <div className="metric-value">
                  {results.daysOfRunway === 999 ? '90+' : animatedRunway}
                </div>
                <div className="metric-label">Days of Cash Runway</div>
              </div>
              
              <div className="metric-card hover-lift">
                <div className="metric-value">
                  ${animatedBurn.toLocaleString()}
                </div>
                <div className="metric-label">Daily Cash Burn</div>
              </div>
              
              <div className="metric-card hover-lift">
                <div 
                  className="metric-value"
                  style={{
                    color: results.riskScore >= 70 ? '#dc2626' : results.riskScore >= 40 ? '#ea580c' : '#16a34a'
                  }}
                >
                  {animatedRisk}/100
                </div>
                <div className="metric-label">Risk Score ({results.riskLevel})</div>
              </div>
            </div>
          </div>

          {/* THE DIAGNOSIS */}
          <div className="diagnosis-section">
            <h3 className="section-heading">Your Top Cash Leaks</h3>
            <p className="body-text">These are the specific issues bleeding cash from your business:</p>
            
            {leaks.map((leak, index) => (
              <div key={index} className="leak-card">
                <div className="leak-header">
                  <span className="leak-number">{index + 1}</span>
                  <h4 className="leak-title">{leak.title}</h4>
                </div>
                <p className="leak-description">{leak.description}</p>
              </div>
            ))}
          </div>

          {/* THE EDUCATION */}
          <div className="education-section">
            <h3 className="section-heading">What This Means</h3>
            
            {leaks.map((leak, index) => (
              <div key={index} className="education-card">
                <h4 className="education-title">
                  <span className="orange-dot">●</span> {leak.title}
                </h4>
                <p className="body-text"><strong>Why it matters:</strong> {leak.education}</p>
                <p className="benchmark-text">
                  <strong>What good looks like:</strong> {leak.benchmark}
                </p>
              </div>
            ))}
          </div>

          {/* WHAT TO IMPLEMENT FIRST */}
          <div className="action-section">
            <h3 className="section-heading">What You Should Implement First</h3>
            <p className="body-text">For each leak, here's ONE immediate action you can take this week:</p>
            
            {leaks.map((leak, index) => (
              <div key={index} className="action-card">
                <div className="action-number">{index + 1}</div>
                <div>
                  <h4 className="action-title">{leak.title}</h4>
                  <p className="action-text">{leak.action}</p>
                </div>
              </div>
            ))}
          </div>

          {/* THE CTA */}
          <div className="cta-section">
            <h3 className="cta-heading">Want this analysis in your inbox?</h3>
            <p className="cta-text">
              Get your complete cash flow breakdown emailed to you, plus our free guide: 
              <strong> "3 Quick Wins to Plug Cash Leaks This Week"</strong>
            </p>
            
            <div style={{
              maxWidth: '500px',
              margin: '32px auto',
              padding: '32px',
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
              <input
                type="text"
                placeholder="Your name"
                id="firstName"
                style={{
                  width: '100%',
                  padding: '14px',
                  fontSize: '16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  marginBottom: '16px',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#2c5282'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
              
              <input
                type="email"
                placeholder="Your email"
                id="emailAddress"
                style={{
                  width: '100%',
                  padding: '14px',
                  fontSize: '16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  marginBottom: '16px',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#2c5282'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
              
              <button 
                className="cta-button"
                onClick={async () => {
                  const firstName = document.getElementById('firstName').value;
                  const email = document.getElementById('emailAddress').value;
                  
                  if (!email || !email.includes('@')) {
                    alert('Please enter a valid email address');
                    return;
                  }
                  
                  // Prepare data to send to Kit
                  const kitData = {
                    api_key: 'PvrbQvKxoOwKrnPMDpN4QQ',
                    email: email,
                    first_name: firstName || 'there',
                    fields: {
                      runway_days: results.daysOfRunway === 999 ? '90+' : results.daysOfRunway.toString(),
                      daily_burn: Math.abs(results.dailyBurn).toString(),
                      monthly_burn: Math.abs(results.monthlyBurn).toString(),
                      risk_score: results.riskScore.toString(),
                      risk_level: results.riskLevel
                    }
                  };
                  
                  // Add leak data
                  leaks.forEach((leak, index) => {
                    const num = index + 1;
                    kitData.fields[`leak_${num}_title`] = leak.title;
                    kitData.fields[`leak_${num}_description`] = leak.description;
                    kitData.fields[`leak_${num}_education`] = leak.education;
                    kitData.fields[`leak_${num}_action`] = leak.action;
                  });
                  
                  try {
                    // Send to Kit
                    const response = await fetch('https://api.convertkit.com/v3/forms/9026361/subscribe', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify(kitData)
                    });
                    
                    if (response.ok) {
                      alert('✓ Success! Check your email for your analysis and quick wins guide.');
                    } else {
                      alert('Something went wrong. Please try again or email us directly.');
                    }
                  } catch (error) {
                    console.error('Error:', error);
                    alert('Something went wrong. Please try again or email us directly.');
                  }
                }}
                style={{width: '100%'}}
              >
                Email Me My Results
              </button>
              
              <p style={{
                fontSize: '12px',
                color: '#64748b',
                marginTop: '12px',
                textAlign: 'center'
              }}>
                We respect your privacy. Unsubscribe anytime.
              </p>
            </div>
            
            <p style={{
              fontSize: '14px',
              color: '#64748b',
              fontStyle: 'italic',
              marginTop: '24px'
            }}>
              P.S. Interested in fixing this systematically? We're accepting 10 founding members 
              for our first Cash Flow Sprint cohort ($1,000 founding rate, regular $2,000). 
              Applications close February 28th. Details in your inbox.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default App;
